import { Plus, Trash2 } from 'lucide-react';
import Button from '../ui/Button';
import { gradesProblem, gradesTotal, GRADE_ORDINAL, type GradeDraft } from '../../utils/treeTypeGrades';

/**
 * The grades a tree type comes in, best first, each with the share of a crop
 * expected at it before grading. The shares add up to 100; the forecast splits
 * a growing crop across the grades by them until it is graded.
 *
 * Controlled: the parent holds the rows and saves them with the tree type.
 */

interface Props {
  rows: GradeDraft[];
  onChange: (rows: GradeDraft[]) => void;
  disabled?: boolean;
}

function TreeTypeGradesEditor({ rows, onChange, disabled }: Props) {
  const set = (i: number, patch: Partial<GradeDraft>) =>
    onChange(rows.map((g, n) => (n === i ? { ...g, ...patch } : g)));
  const add = () => {
    const left = Math.max(0, 100 - gradesTotal(rows));
    onChange([...rows, { name: GRADE_ORDINAL[rows.length] ?? `Grade ${rows.length + 1}`, description: null, expected_share_pct: rows.length ? left : 100 }]);
  };
  const remove = (i: number) => onChange(rows.filter((_, n) => n !== i));
  const problem = gradesProblem(rows);

  return (
    <div className="space-y-2">
      {rows.length === 0 ? (
        <p className="text-xs text-neutral-500">
          No grades: every tree of this type sells as one. Add grades if the crop is graded (1st, 2nd…) when it is lifted.
        </p>
      ) : (
        <ol className="space-y-2">
          {rows.map((g, i) => (
            <li key={i} className="border border-neutral-200 rounded-lg p-2 space-y-2">
              <div className="flex flex-wrap items-end gap-2">
                <span className="text-xs font-semibold text-neutral-500 w-6 pb-2">{i + 1}</span>
                <label className="flex-1 min-w-[8rem] text-xs text-neutral-600">
                  Name
                  <input
                    type="text" maxLength={50} value={g.name} disabled={disabled}
                    onChange={(e) => set(i, { name: e.target.value })}
                    className="mt-0.5 w-full border border-neutral-300 rounded-lg px-2 py-1.5 text-sm"
                  />
                </label>
                <label className="w-24 text-xs text-neutral-600">
                  Expected %
                  <input
                    type="text" inputMode="decimal" value={String(g.expected_share_pct)} disabled={disabled}
                    aria-label={`Expected share of grade ${i + 1}`}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9.]/g, '');
                      set(i, { expected_share_pct: v === '' ? 0 : Number(v) });
                    }}
                    className="mt-0.5 w-full border border-neutral-300 rounded-lg px-2 py-1.5 text-sm"
                  />
                </label>
                {!disabled && (
                  <button
                    type="button" onClick={() => remove(i)} aria-label={`Remove grade ${i + 1}`}
                    className="p-2 text-neutral-400 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <label className="block text-xs text-neutral-600">
                What makes it this grade (optional)
                <input
                  type="text" maxLength={500} value={g.description ?? ''} disabled={disabled}
                  placeholder="e.g. 12 mm calliper and over"
                  onChange={(e) => set(i, { description: e.target.value || null })}
                  className="mt-0.5 w-full border border-neutral-300 rounded-lg px-2 py-1.5 text-sm"
                />
              </label>
            </li>
          ))}
        </ol>
      )}
      <div className="flex flex-wrap items-center gap-3">
        {!disabled && rows.length < 9 && (
          <Button type="button" variant="outline" size="sm" onClick={add}>
            <Plus className="h-4 w-4 mr-1" /> Add a grade
          </Button>
        )}
        {rows.length > 0 && (
          <span className={problem ? 'text-xs text-red-700 break-words min-w-0' : 'text-xs text-neutral-500'}>
            {problem ?? `Shares add up to ${gradesTotal(rows)}%.`}
          </span>
        )}
      </div>
    </div>
  );
}

export default TreeTypeGradesEditor;
